import type { PlaybookVersion } from '@onefootprint/request-types/dashboard';
import { LoadingSpinner, useConfirmationDialog } from '@onefootprint/ui';
import { ReactFlowProvider } from '@xyflow/react';
import { useRouter } from 'next/router';
import { useQueryState } from 'nuqs';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ErrorComponent from '@/components/error';
import { useStore } from '@/components/playbook-builder';
import useConfirmClose from '@/components/playbook-builder/hooks/use-confirm-close';
import TenantSwitcher from '@/components/tenant-switcher';
import useInProgressEditSessionStorage from '@/hooks/use-in-progress-edit-session-storage';
import usePlaybookId from '@/pages/playbooks/hooks/use-playbook-id';
import PlaybookDraftEdit from './components/playbook-draft-edit';
import useAdoptEditSession from './hooks/use-adopt-edit-session';
import useDeepLinkedDraft from './hooks/use-deep-linked-draft';
import usePercyPlaybookSync from './hooks/use-percy-playbook-sync';
import { usePlaybook } from './hooks/use-playbook';
import PlaybookEdit from './playbook-edit';
const PlaybookEditContainer = () => {
  const {
    t
  } = useTranslation('playbook-builder', {
    keyPrefix: 'view'
  });
  const router = useRouter();
  const store = useStore();
  const playbookId = usePlaybookId();
  const confirm = useConfirmationDialog();
  const {
    getEditSessionId,
    clearEditSessionId,
    clearAllEditSessions
  } = useInProgressEditSessionStorage(playbookId);
  const [editSessionIdParam, setEditSessionIdParam] = useQueryState('editSessionId');
  const {
    isDraft,
    committedEnabled
  } = useDeepLinkedDraft(playbookId, editSessionIdParam);
  const {
    playbook,
    selectedVersion,
    selectedVersionNoGraph,
    playbookVersions,
    isPending,
    error,
    refetch
  } = usePlaybook(playbookId, {
    enabled: committedEnabled,
    onChangeVersion: (playbookVersion: PlaybookVersion) => store.reset({
      obc: playbookVersion
    })
  });
  const adoptEditSession = useAdoptEditSession(playbookVersions);
  usePercyPlaybookSync(playbookVersions);
  const {
    data: {
      history,
      playbookId: loadedPlaybookId
    }
  } = store;
  useEffect(() => {
    router.beforePopState(({
      as
    }) => {
      if (as?.includes('/playbooks/new')) {
        router.replace('/playbooks');
        return false;
      }
      return true;
    });
    return () => {
      router.beforePopState(() => true);
    };
  }, [router]);

  // biome-ignore lint/plugin: deps [router] deliberately narrower — intentional freeze
  const onNavigateBack = useCallback(() => {
    // delete all the in-progress edit sessions for this playbook
    clearAllEditSessions();
    router.back();
    // If there's nothing to go back to, we will execute this, which will just bring us to the playbooks page
    router.push('/playbooks');
  }, [router]);
  const {
    attemptClose
  } = useConfirmClose({
    isDirty: history.hasChanges,
    onCloseParent: onNavigateBack,
    title: t($ => $.confirmBeforeBack.title),
    description: t($_0 => $_0.confirmBeforeBack.description)
  });
  const handleClose = () => {
    attemptClose.handler();
  };
  const resetStore = () => {
    if (selectedVersion) {
      store.reset({
        obc: selectedVersion
      });
    }
  };

  // Reset store when version changes
  useEffect(() => {
    resetStore();
  }, [selectedVersion]);

  // Percy's deep-link carries ?editSessionId=… (alongside &version=…) for an uncommitted draft
  // session; adopt it so the human lands on that draft. Runs after the reset effect so the draft
  // graph wins over the linked version's. The param is consumed before the fetch so a version
  // switch mid-flight can't re-fire the adoption; a failed fetch surfaces its error toast.
  useEffect(() => {
    if (!selectedVersion || !editSessionIdParam) return;
    confirm.hide();
    const adoptedSessionId = editSessionIdParam;
    setEditSessionIdParam(null);
    void adoptEditSession(adoptedSessionId);
  }, [selectedVersion, editSessionIdParam]);

  // If the user has an abandoned edit session, prompt to see if they want to continue where they left off
  useEffect(() => {
    // Deep-link adoption (above) takes precedence; skip the resume prompt.
    if (!selectedVersion || editSessionIdParam) return;
    const storedSessionId = getEditSessionId(selectedVersion.id);
    if (!storedSessionId) {
      return;
    }
    confirm.open({
      title: t($_1 => $_1.restoreSession.title),
      description: t($_2 => $_2.restoreSession.description),
      variant: 'default',
      primaryButton: {
        label: t($_3 => $_3.restoreSession.confirm),
        onClick: async () => {
          await adoptEditSession(storedSessionId);
        }
      },
      secondaryButton: {
        label: t($_4 => $_4.restoreSession.cancel),
        onClick: () => clearEditSessionId(selectedVersion.id)
      }
    });
  }, [selectedVersion]);

  // Warn user before leaving page if there are pending changes
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (history.hasChanges) {
        event.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [history.hasChanges]);

  // The adopted session is an unpublished draft — render the store-driven editor (no committed data to fetch).
  if (isDraft) {
    return <PlaybookDraftEdit exit="close" onExit={() => router.push('/playbooks')} onCreated={id => router.replace(`/playbooks/${id}`)} />;
  }
  if (error) {
    const loading = <div className="flex h-full items-center justify-center md:h-screen">
        <LoadingSpinner />
      </div>;
    return <TenantSwitcher id={playbookId} Loading={() => loading} onSwitched={data => {
      if (data.context.object !== 'playbook') return;
      // If a playbook version / key was provided instead of an ID, update the route
      const {
        playbookId: canonicalId,
        versionId
      } = data.context;
      if (canonicalId !== playbookId) {
        router.replace(`/playbooks/${canonicalId}?versionId=${versionId}`);
      }
    }}>
        <ErrorComponent error={error} />
      </TenantSwitcher>;
  }

  // While a deep-linked draft is adopting, its committed queries are disabled and stay `pending`, so this
  // loader covers that wait too.
  if (isPending) {
    return <div className="flex h-full items-center justify-center md:h-screen">
        <LoadingSpinner />
      </div>;
  }

  // Early return if data not ready
  if (!playbook || !selectedVersionNoGraph) {
    return null;
  }

  // Until the store resets to the URL's playbook, it still holds the previous one's graph — spin rather
  // than flash it. Compare playbookId, not the version id (editing spins up a new draft version).
  if (loadedPlaybookId !== playbookId) {
    return <div className="flex h-full items-center justify-center md:h-screen">
        <LoadingSpinner />
      </div>;
  }
  return (
    // Fills the FullLayout <main> (no longer a fixed full-viewport overlay) so the global nav stays visible.
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <ReactFlowProvider>
        <PlaybookEdit canEdit={store.data.canEdit} onClose={handleClose} onRefetch={refetch} playbook={playbook} playbookVersions={playbookVersions} selectedVersionNoGraph={selectedVersionNoGraph} settings={store.data.settings} />
      </ReactFlowProvider>
    </div>
  );
};
export default PlaybookEditContainer;